<template>
  <div>
    <div class="text-subtitle-2 mb-1">Theme</div>
    <div class="text-caption text-medium-emphasis mb-3">
      Pick a starting point — fine-tune the colors and font below.
      <v-chip v-if="modelValue === 'custom'" class="ml-1" color="primary" size="x-small" variant="tonal">
        Custom
      </v-chip>
    </div>
    <div aria-label="Invoice theme" class="invoice-theme-grid" role="radiogroup">
      <button
        v-for="theme in invoiceThemes"
        :key="theme.key"
        :aria-checked="modelValue === theme.key"
        :class="['invoice-theme-card', {'invoice-theme-card--active': modelValue === theme.key}]"
        role="radio"
        type="button"
        @click="select(theme)"
      >
        <span :style="swatchStyle(theme)" class="invoice-theme-swatch">Aa</span>
        <span class="invoice-theme-label">{{ theme.label }}</span>
        <span class="invoice-theme-desc">{{ theme.description }}</span>
        <v-icon v-if="modelValue === theme.key" class="invoice-theme-check" color="primary" size="small">
          mdi-check-circle
        </v-icon>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {INVOICE_THEMES, type InvoiceThemePreset} from '@/config/invoiceThemes';

defineProps<{
  /** The active theme key. */
  modelValue: string | undefined;
}>();

const emit = defineEmits<{
  'update:modelValue': [key: string];
  /** The full preset, so the parent can load its color/font/template values. */
  apply: [preset: InvoiceThemePreset];
}>();

const invoiceThemes = INVOICE_THEMES;

const select = (theme: InvoiceThemePreset): void => {
  emit('update:modelValue', theme.key);
  emit('apply', theme);
};

const swatchStyle = (theme: InvoiceThemePreset): Record<string, string> => ({
  background: theme.primary_color,
  color: '#ffffff',
  borderColor: theme.secondary_color,
});
</script>

<style scoped>
.invoice-theme-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.invoice-theme-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px;
  text-align: left;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.invoice-theme-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.5);
}

.invoice-theme-card--active {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}

.invoice-theme-card:last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

.invoice-theme-swatch {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 44px;
  border-radius: 6px;
  border: 1px solid;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.invoice-theme-label {
  font-size: 0.875rem;
  font-weight: 600;
}

.invoice-theme-desc {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.6;
  line-height: 1.3;
}

.invoice-theme-check {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style>
